<script lang="ts">
import { Users, Handshake, Ellipsis } from 'lucide-vue-next';

export default {
  name: 'TowerCard',
  components: {
    Users,
    Handshake,
    Ellipsis,
  },
};
</script>

<script setup lang="ts">
import { computed, ref, onBeforeMount } from 'vue';
import { TowerCardData, TowerType } from '@/views/tower_dashboard/types/tower-dashboard';
import { DateTime } from 'luxon';
import { AppState } from '@/store';
import { useStore } from 'vuex';
import { IsUserABroker, IsUserAnAdmin, IsUserAClient } from '@/utilities/AuthUtils';

const props = defineProps<{
  cardData: TowerCardData;
  optionsOpen?: boolean;
  highlightTowerId?: string;
  selected?: boolean;
}>();

const emit = defineEmits<{
  (e: 'click', towerId: string, selected: boolean): void;
  (e: 'edit', towerId: string): void;
  (e: 'rename', towerId: string): void;
  (e: 'duplicate', towerId: string): void;
  (e: 'toggle-publish', towerId: string, published: boolean): void;
  (e: 'delete', towerCard: TowerCardData): void;
  (e: 'update:optionsOpen', optionsOpen: boolean): void;
  (e: 'update:selected', selected: boolean): void;
}>();

const store = useStore<AppState>();

interface TowerTypeChip {
  bgColor: string;
  textColor: string;
  text: string;
}

const towerChips: Record<TowerType, TowerTypeChip> = {
  [TowerType.BoundCoverage]: { bgColor: 'tw:bg-blue-200', textColor: 'tw:text-blue-900', text: 'Bound Coverage' },
  [TowerType.Renewal]: { bgColor: 'tw:bg-red-200', textColor: 'tw:text-red-900', text: 'Renewal' },
  [TowerType.NewCoverage]: { bgColor: 'tw:bg-orange-200', textColor: 'tw:text-orange-900', text: 'New Coverage' },
  [TowerType.Scratch]: { bgColor: 'tw:bg-emerald-200', textColor: 'tw:text-emerald-900', text: 'Scratch' },
  [TowerType.MultiTower]: { bgColor: 'tw:bg-[#d98ad2]', textColor: 'tw:text-white', text: 'MultiTower' },
};

const showOptions = ref(false);
const optionsMenu = ref<HTMLElement>();
const showContributors = ref<boolean>(false);
const cardSelected = ref<boolean>(false);

onBeforeMount(() => {
  cardSelected.value = props.selected ?? false;
});

// ---------
// computed
// ---------
const versionLabel = computed(() => {
  if (props.cardData.versionLabel) {
    if (props.cardData.versionLabel.length > 60) {
      return props.cardData.versionLabel.substring(0, 60).trim() + '...';
    }
    return props.cardData.versionLabel;
  }
  return 'Initial Version';
});

const programName = computed(() => {
  let programName = '';
  if (props.cardData.program) {
    const program = props.cardData.program;
    
    if (program.type === 'Manual') {
      programName = `Manual ${program.year} ${program.name}`;
    } else {
      programName = program.name;
    }

    if (programName) {
      if (programName.length > 50) {
        return programName.substring(0, 50).trim() + '...';
      }
    }
    
    return programName;
  }

  return '';
});

const towerName = computed(() => {
  if (props.cardData.towerName) {
    if (props.cardData.towerName.length > 50) {
      return props.cardData.towerName.substring(0, 50).trim() + '...';
    }
    return props.cardData.towerName;
  }
  return '';
});

const userEmail = computed(() => {
  if (props.cardData.user.userEmail) {
    if (props.cardData.user.userEmail.length > 50) {
      return props.cardData.user.userEmail.substring(0, 50).trim() + '...';
    }

    return props.cardData.user.userEmail;
  }
  return '';
});

const userName = computed(() => {
  if (props.cardData.user.name) {
    if (props.cardData.user.name.length > 50) {
      return props.cardData.user.name.substring(0, 50).trim() + '...';
    }

    return props.cardData.user.name;
  }
  return '';
});

const canDelete = computed(() => {
  if (props.cardData.published) {
    return false;
  }

  if (IsUserAnAdmin(store.state.user)) {
    return true;
  }

  if (IsUserABroker(store.state.user) && store.state.user?.user.id === props.cardData.user.id) {
    return true;
  }

  return false;
});

const updatedOn = computed(() => {
  if (props.cardData.updatedOn) {
    let date = DateTime.fromISO(props.cardData.updatedOn);

    if (date.startOf('day').equals(DateTime.now().startOf('day'))) {
      return date.toLocaleString(DateTime.TIME_SIMPLE);
    }

    return date.toLocaleString(DateTime.DATE_MED) + ' - ' + date.toLocaleString(DateTime.TIME_SIMPLE);
  }
  return '';
});

const isMultiTower = computed(() => {
  return props.cardData.towerTypeId === TowerType.MultiTower;
});

const multiTowerTowerCards = computed(() => {
  if (props.cardData.towerCards) {
    return props.cardData.towerCards;
  }
  return [];
});

const highlightCard = computed(() => {
  return props.highlightTowerId === props.cardData.towerId;
});

const isUserAClient = computed(() => {
  return IsUserAClient(store.state.user);
});

// ---------
// methods
// ---------
function handleCardClick() {
  cardSelected.value = !cardSelected.value;
  emit('click', props.cardData.towerId, cardSelected.value);
}

function handleEditTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('edit', props.cardData.towerId);
}

function handleRenameTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('rename', props.cardData.towerId);
}

function handleDuplicateTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('duplicate', props.cardData.towerId);
}

function handleDeleteTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('delete', props.cardData);
}

function handlePublishTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('toggle-publish', props.cardData.towerId, !props.cardData.published);
}

function toggleOptionsMenu(e: Event) {
  e.stopPropagation();
  showOptions.value = !showOptions.value;
  emit('update:optionsOpen', showOptions.value);

  // set focus on open for blur to work
  if (showOptions.value) {
    setTimeout(() => {
      optionsMenu.value?.focus();
    }, 50);
  }
}

function handleOptionsBlur(e: FocusEvent) {
  if (showOptions.value) {
    emit('update:optionsOpen', false);
    showOptions.value = false;
  }
}
</script>

<template>
  <div class="group tw:group tw:pointer-events-auto tw:relative tw:flex tw:cursor-pointer">
    <div
      @click="handleCardClick"
      class="tw:group tw:pointer-events-auto tw:relative tw:flex tw:h-56 tw:w-80 tw:cursor-pointer tw:flex-col tw:justify-between tw:rounded-md tw:border tw:border-solid tw:px-2 tw:py-2 tw:text-[#133044] tw:shadow-md tw:group-hover:border-blue-200 tw:group-hover:bg-blue-50 tw:group-hover:shadow-none"
      :class="{
        'tw:border-2 tw:border-blue-500 tw:bg-blue-50 tw:shadow-none tw:group-hover:border-blue-500': cardSelected,
        'tw:border-gray-300 tw:bg-white': !cardSelected,
        'tw:border-red-500': highlightCard,
      }"
    >
      <div class="tw:flex tw:justify-between">
        <div class="tw:flex tw:gap-2 tw:overflow-hidden tw:px-2">
          <p :title="cardData.versionLabel" class="tw:grow tw:break-words tw:text-base tw:font-medium">
            {{ versionLabel }}
          </p>
        </div>

        <div v-if="!isUserAClient"
          ref="optionsMenu"
          tabindex="0"
          @blur="handleOptionsBlur"
          @click="toggleOptionsMenu"
          class="tw:z-10 tw:flex tw:h-8 tw:w-9 tw:cursor-pointer tw:place-content-center tw:overflow-hidden tw:rounded-md tw:bg-white tw:hover:bg-gray-100"
        >
          <Ellipsis class="tw:m-auto tw:text-gray-400" />

          <div v-if="showOptions" class="tw:absolute tw:right-2 tw:top-11">
            <ul class="tw:menu tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 tw:bg-white tw:shadow-xs">
              <li>
                <a @click="handleEditTower" class="tw:text-black">Edit</a>
                <a @click="handleRenameTower" class="tw:text-black">Rename</a>
                <a @click="handleDuplicateTower" class="tw:text-black">Duplicate</a>
                <a @click="handlePublishTower" class="tw:text-black">{{
                  cardData.published ? 'Unpublish' : 'Publish to Clients'
                }}</a>
                <a v-if="canDelete" @click="handleDeleteTower" class="tw:text-primary">Delete</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div
        class="tw:bg tw:align-center tw:flex tw:h-6 tw:w-fit tw:justify-center tw:rounded-lg tw:px-3"
        :class="
          towerChips[cardData.towerTypeId as TowerType].bgColor +
          ' ' +
          towerChips[cardData.towerTypeId as TowerType].textColor
        "
      >
        <span class="tw:my-auto">{{ towerChips[cardData.towerTypeId as TowerType].text }}</span>
      </div>

      <div class="tw:my-4 tw:ml-2">
        <div
          v-if="isMultiTower"
          class="tw:flex tw:w-fit tw:flex-col tw:gap-1 tw:rounded tw:border tw:border-solid tw:border-gray-200 tw:p-1 tw:text-xs tw:text-gray-600"
        >
          <div v-for="towerCard in multiTowerTowerCards" :key="towerCard.towerId" class="tw:flex tw:gap-1">
            <span class="tw:font-bold">{{ towerCard.versionLabel }}</span>
            <span v-if="towerCard.towerTypeId === TowerType.MultiTower" class="tw:italic tw:text-[#d98ad2]"
              >(MultiTower)</span
            >
            <span v-else class="tw:italic"
              >({{
                towerCard.program && towerCard.program.name !== '' ? towerCard.program.name : towerCard.towerName
              }})</span
            >
          </div>
        </div>
        <div v-else class="tw:text-base tw:text-gray-600">
          {{ programName && programName !== '' ? programName : towerName }}
        </div>
      </div>

      <div
        class="tw:mt-auto tw:flex tw:w-full tw:flex-row tw:items-end tw:justify-between tw:gap-2 tw:px-1 tw:text-end tw:text-xs"
      >
        <div
          v-if="cardData.published && !isUserAClient"
          class="tw:tooltip tw:tooltip-top tw:z-20 tw:flex tw:justify-end"
          data-tip="Published to Clients"
        >
          <Users class="tw:self-end tw:text-green-500" :size="20" />
        </div>

        <div
          v-if="props.cardData.contributors && props.cardData.contributors.length > 0"
          class="tw:relative tw:flex tw:justify-end"
        >
          <Handshake
            :size="20"
            class="tw:self-end tw:text-gray-400"
            @mouseover="showContributors = true"
            @mouseleave="showContributors = false"
          />
          <div
            v-show="showContributors"
            class="tw:absolute tw:left-0 tw:top-5 tw:z-50 tw:mt-1 tw:whitespace-nowrap tw:rounded-md tw:border tw:border-gray-300 tw:bg-white tw:px-4 tw:py-2 tw:text-start tw:shadow-md"
          >
            <p class="tw:mb-2 tw:text-sm tw:font-bold">Contributors</p>
            <ul class="tw:m-0 tw:list-none tw:p-0">
              <li
                v-for="(contributor, index) in props.cardData.contributors.split(',')"
                :key="index"
                class="tw:text-sm"
              >
                {{ contributor }}
              </li>
            </ul>
          </div>
        </div>

        <div class="tw:ml-auto tw:mr-0 tw:flex tw:flex-col">
          <p v-if="!isUserAClient" class="tw:mb-0 tw:mt-auto tw:break-all">{{ userName !== '' ? userName : userEmail }}</p>
          <p class="tw:font tw:mb-0 tw:text-gray-500">Updated {{ updatedOn }}</p>
        </div>
      </div>
    </div>
  </div>
</template>
